import React, { useState } from 'react';
import { HiOutlineSearch, HiOutlineTag, HiOutlineCurrencyDollar } from 'react-icons/hi';
import { getAuthToken } from '../services/auth';
import { useAuth } from '../context/AuthContext';
import { calculateOrderTotal } from '../utils/helpers';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ManagementDiscounts = () => {
    const [orderId, setOrderId] = useState('');
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [discountAmount, setDiscountAmount] = useState('');
    const [discountPercentage, setDiscountPercentage] = useState('');
    const [discountLoading, setDiscountLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Fetch order by ID
    const handleSearchOrder = async () => {
        if (!orderId.trim()) {
            setError('Please enter an order ID');
            return;
        }

        setLoading(true);
        setError('');
        setOrder(null);
        setSuccessMessage('');

        try {
            const token = getAuthToken();
            const response = await fetch(`${API_BASE_URL}/orders/all-dealers/${orderId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Order not found');
                } else if (response.status === 403) {
                    throw new Error('Access denied. You may not have permission to view this order.');
                } else if (response.status === 400) {
                    throw new Error('Bad request. Please check the order ID.');
                } else {
                    throw new Error(`Failed to fetch order: ${response.status}`);
                }
            }

            const data = await response.json();
            setOrder(data);
        } catch (err) {
            console.error('Error fetching order:', err);
            setError(err.message || 'Failed to fetch order');
        } finally {
            setLoading(false);
        }
    };

    // Apply discount by amount
    const handleDiscountByAmount = async () => {
        if (!discountAmount || parseFloat(discountAmount) <= 0) {
            setError('Please enter a valid discount amount');
            return;
        }

        setDiscountLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const token = getAuthToken();
            const response = await fetch(
                `${API_BASE_URL}/order-price/${orderId}/discount/amount?amount=${discountAmount}`,
                {
                    method: 'POST',
                    headers: {
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to apply discount: ${response.status}`);
            }

            setSuccessMessage(`Discount of ${discountAmount} IQD applied successfully!`);
            setDiscountAmount('');
            // Refresh the order data
            await handleSearchOrder();
        } catch (err) {
            console.error('Error applying discount by amount:', err);
            setError(err.message || 'Failed to apply discount');
        } finally {
            setDiscountLoading(false);
        }
    };

    // Apply discount by percentage
    const handleDiscountByPercentage = async () => {
        if (!discountPercentage || parseFloat(discountPercentage) <= 0 || parseFloat(discountPercentage) > 100) {
            setError('Please enter a valid discount percentage (1-100)');
            return;
        }

        setDiscountLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const token = getAuthToken();
            const response = await fetch(
                `${API_BASE_URL}/order-price/${orderId}/discount/percentage?percentage=${discountPercentage}`,
                {
                    method: 'POST',
                    headers: {
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to apply discount: ${response.status}`);
            }

            setSuccessMessage(`Discount of ${discountPercentage}% applied successfully!`);
            setDiscountPercentage('');
            // Refresh the order data
            await handleSearchOrder();
        } catch (err) {
            console.error('Error applying discount by percentage:', err);
            setError(err.message || 'Failed to apply discount');
        } finally {
            setDiscountLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearchOrder();
        }
    };

    const { user } = useAuth();
    const allowedDiscountUsers = ['mohammed_ali', 'mohammed_jabali'];

    if (!user || !user.username || !allowedDiscountUsers.includes(user.username)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center p-8 bg-white rounded-lg shadow-md border border-gray-200">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h1>
                    <p className="text-gray-600">You do not have permission to access the discounts management page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50/50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4 tracking-tight">
                        Order Discounts
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Manage pricing adjustments efficiently. Search for an order specific order and apply precise discounts by amount or percentage.
                    </p>
                </div>

                {/* Search Section */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-blue-50 rounded-full opacity-50 blur-3xl pointer-events-none"></div>

                    <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <span className="p-2 bg-blue-100 rounded-lg text-blue-600">
                            <HiOutlineSearch className="w-5 h-5" />
                        </span>
                        Find Order
                    </h2>

                    <div className="flex flex-col md:flex-row gap-4 relative z-10">
                        <div className="flex-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <span className="text-gray-400 font-medium">#</span>
                            </div>
                            <input
                                id="orderId"
                                type="text"
                                value={orderId}
                                onChange={(e) => setOrderId(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Enter Order ID (e.g., 12345)"
                                className="w-full pl-8 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium text-gray-900 placeholder-gray-400"
                            />
                        </div>
                        <button
                            onClick={handleSearchOrder}
                            disabled={loading}
                            className={`px-8 py-4 rounded-xl font-bold text-white shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2 transition-all transform active:scale-95 ${loading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:-translate-y-0.5'
                                }`}
                        >
                            {loading ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>Searching...</span>
                                </>
                            ) : (
                                <>
                                    <HiOutlineSearch className="text-xl" />
                                    <span>Search</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Alerts */}
                {error && (
                    <div className="mb-8 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-xl shadow-sm flex items-start gap-3">
                        <div className="text-red-500 mt-0.5">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-red-800 font-bold text-sm">Error</h3>
                            <p className="text-red-700 text-sm mt-1">{error}</p>
                        </div>
                    </div>
                )}

                {successMessage && (
                    <div className="mb-8 bg-green-50 border-l-4 border-green-500 p-4 rounded-r-xl shadow-sm flex items-start gap-3 animate-fade-in-up">
                        <div className="text-green-500 mt-0.5">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-green-800 font-bold text-sm">Success</h3>
                            <p className="text-green-700 text-sm mt-1">{successMessage}</p>
                        </div>
                    </div>
                )}

                {/* Order Details & Actions */}
                {order ? (
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animation-fade-in relative">

                        {/* Order Info Header */}
                        <div className="p-8 border-b border-gray-100 bg-gray-50/50">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h2 className="text-2xl font-bold text-gray-900">Order #{order.id}</h2>
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                                            ${order.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                                                order.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                                    order.status === 'CANCELLED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                                            {order.status}
                                        </span>
                                    </div>
                                    <p className="text-gray-500 text-sm flex items-center gap-2">
                                        <span>Created: {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}</span>
                                    </p>
                                </div>
                                <div className="text-right bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">Total Amount</p>
                                    <p className="text-3xl font-black text-gray-900 tracking-tight">
                                        {calculateOrderTotal(order) !== null ? (
                                            <>
                                                {calculateOrderTotal(order).toLocaleString()} <span className="text-lg text-gray-500 font-medium">IQD</span>
                                            </>
                                        ) : (
                                            'N/A'
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* Client Info Pill */}
                            {(order.clientName || order.assignedDealer) && (
                                <div className="mt-6 flex flex-wrap gap-3">
                                    {order.clientName && (
                                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium border border-blue-100">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            {order.clientName}
                                        </div>
                                    )}
                                    {order.assignedDealer && (
                                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium border border-purple-100">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            {order.assignedDealer}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Discount Controls */}
                        <div className="p-8">
                            <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                                <HiOutlineTag className="text-blue-600" />
                                Apply New Discount
                            </h3>

                            {order.discountDetails && (Number(order.discountDetails.discountByAmount) > 0 || Number(order.discountDetails.discountByPercentage) > 0) ? (
                                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl shadow-sm flex items-start gap-3 mb-6">
                                    <div className="text-amber-500 mt-0.5">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-amber-800 font-bold text-sm">Discount Already Applied</h3>
                                        <p className="text-amber-700 text-sm mt-1">This order already has a discount applied. Only one discount can be applied per order.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* By Amount */}
                                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-green-300 transition-colors group">
                                        <label className="block text-sm font-bold text-gray-700 mb-3 group-hover:text-green-700 transition-colors">
                                            Fixed Amount Discount
                                        </label>
                                        <div className="space-y-4">
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <span className="text-gray-500 font-bold">IQD</span>
                                                </div>
                                                <input
                                                    type="number"
                                                    value={discountAmount}
                                                    onChange={(e) => setDiscountAmount(e.target.value)}
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="1000"
                                                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                />
                                            </div>
                                            <button
                                                onClick={handleDiscountByAmount}
                                                disabled={discountLoading}
                                                className="w-full py-3 bg-green-600 text-white rounded-lg font-bold shadow-lg shadow-green-500/20 hover:bg-green-700 hover:shadow-green-500/30 transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <HiOutlineCurrencyDollar className="text-lg" />
                                                Apply Amount
                                            </button>
                                        </div>
                                    </div>

                                    {/* By Percentage */}
                                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-purple-300 transition-colors group">
                                        <label className="block text-sm font-bold text-gray-700 mb-3 group-hover:text-purple-700 transition-colors">
                                            Percentage Discount
                                        </label>
                                        <div className="space-y-4">
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    value={discountPercentage}
                                                    onChange={(e) => setDiscountPercentage(e.target.value)}
                                                    placeholder="0 - 100"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    className="w-full pl-4 pr-12 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all font-medium"
                                                />
                                                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                                                    <span className="text-gray-500 font-bold">%</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleDiscountByPercentage}
                                                disabled={discountLoading}
                                                className="w-full py-3 bg-purple-600 text-white rounded-lg font-bold shadow-lg shadow-purple-500/20 hover:bg-purple-700 hover:shadow-purple-500/30 transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <HiOutlineTag className="text-lg" />
                                                Apply Percentage
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    !loading && !error && (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <HiOutlineSearch className="text-4xl text-gray-300" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Order Selected</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">
                                Search for an order by its ID above to view details and manage pricing discounts.
                            </p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default ManagementDiscounts;
